import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useRef, useState } from 'react'

import EmailAddress from '@/components/misc/EmailAddresses/EmailAddress'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import useNotyf from '@/hooks/useNotyf'
import { faTriangleExclamation } from '@/lib/fontAwesomePro/solid'
import { isValidEmail } from '@/lib/helpers/emailAddressHelpers'
import { cn } from '@/lib/utils'

interface Props {
  /**
   * Called whenever the list of email addresses changes
   *
   * @param newEmails The new set of emails (potentially including invalid ones)
   * @param hasError `true` if `newEmails` contains 1 or more invalid emails
   */
  onChange: (newEmails: string[], hasError: boolean) => void
  emails: string[]
}

const regExpEndsWithSpaceCommaOrSemiColon = /.*(\s|;|,)$/g

/**
 * A nice UI for entering multiple email addresses.
 * - Allows **pasting** in a bunch of email addresses together (separated by `whitespace`,
 *   `,`, `|` or `;` (or a combination))
 * - Allows **typing** email addresses manually one at a time.
 * - Email addresses are tokenised (each with an X button to delete them with a click)
 *   as the user types. This breaks on ` `, `,`, `;`, `tab` and `enter`
 * - Provides a validation UI
 */
const EmailAddresses: React.FC<Props> = ({ emails, onChange }) => {
  const [isFocused, setIsFocused] = useState(false)
  const editableElemRef = useRef<HTMLDivElement>(null)
  const notyf = useNotyf()

  function handleInput(event: React.InputEvent<HTMLDivElement>) {
    // We only really care about the last char, so can just reference textContent
    const textContent = event.currentTarget.textContent

    if (
      textContent !== null &&
      regExpEndsWithSpaceCommaOrSemiColon.test(textContent)
    ) {
      parseTypedInput()
    }
  }

  function handleChange(newEmails: string[]) {
    onChange(newEmails, countErrors(newEmails) > 0)
  }

  function parseTypedInput() {
    if (!editableElemRef.current) {
      return
    }

    const inputText = getTrailingText(true)

    if (inputText.length === 0) {
      return
    }

    const newList = [...emails, inputText.trim()]

    handleChange(newList)

    editableElemRef.current.innerText = ''
  }

  function removeValueByIndex(index: number) {
    const newList = [...emails]

    newList.splice(index, 1)

    handleChange(newList)
  }

  function getTrailingText(trimmed = false): string {
    if (!editableElemRef.current) {
      return ''
    }

    return trimmed
      ? editableElemRef.current.innerText
          .trim()
          // These chars aren't valid for email addresses, and can be used as separators
          .replaceAll(',', '')
          .replaceAll(';', '')
      : editableElemRef.current.innerText
  }

  function removeInvalidEmails() {
    const validEmails = emails.filter(isValidEmail)

    handleChange(validEmails)
  }

  function countErrors(emailAddresses: string[]): number {
    return emailAddresses.filter(
      (emailAddress: string) => !isValidEmail(emailAddress),
    ).length
  }

  const errorCount = countErrors(emails)

  const borderClass =
    errorCount > 0 ? 'border-destructive' : isFocused ? 'border-primary' : ''

  return (
    <>
      <div
        className={cn(
          `relative flex cursor-text flex-wrap gap-1 rounded-md border px-2 pt-2
          pb-12`,
          borderClass,
        )}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('.email-address')) return

          editableElemRef.current?.focus()
        }}
        onFocus={(e) => {
          if ((e.target as HTMLElement).closest('.email-address')) return

          editableElemRef.current?.focus()
        }}
      >
        <Label className="py-1">To:</Label>

        {/* Placeholder */}
        {emails.length === 0 && !isFocused && (
          <span aria-hidden className="py-1 pr-0 pl-2 opacity-50">
            name@company.com
          </span>
        )}

        {emails.map((value, index) => (
          <EmailAddress
            key={`${index}-${value}`}
            value={value}
            isValid={isValidEmail(value)}
            onClickRemove={() => removeValueByIndex(index)}
            onChange={(newValue: string) => {
              const newValues = [...emails]

              newValues[index] = newValue

              handleChange(newValues)
            }}
          />
        ))}

        <span
          contentEditable
          ref={editableElemRef}
          className="py-1 pl-2"
          onInput={handleInput}
          onKeyDown={(event) => {
            switch (event.key) {
              case 'Backspace':
                if (getTrailingText().length === 0) {
                  removeValueByIndex(emails.length - 1)
                }

                break

              case 'Enter':
              case 'Tab':
                if (getTrailingText().length === 0) {
                  return
                }

                // Don't tab away, or submit
                event.preventDefault()

                parseTypedInput()
                editableElemRef.current?.focus()

                break
            }
          }}
          onPaste={(event) => {
            event.preventDefault()

            const newValues = event.clipboardData
              .getData('text')
              .trim()
              // Can be comma, semi-colon, space or pipe separated, possibly with some wqhitespace in there too
              .split(/[,;\s |]+/)
              // 5, because that's the length of "a@b.c"
              .filter((value: string): boolean => value.length >= 5)

            const finalNewValues = newValues
              // Don't bother inserting anything that's already in there
              .filter((emailAddress: string) => !emails.includes(emailAddress))

            const countDuplicateValues =
              newValues.length - finalNewValues.length

            let message = 'Pasted.'

            if (countDuplicateValues > 0) {
              message += ` ${countDuplicateValues} email address${countDuplicateValues === 1 ? '' : 'es'} were already in this list.`
            }

            notyf.success(message)

            // Add them to the list
            handleChange([...emails, ...finalNewValues])
          }}
          onFocus={() => {
            setIsFocused(true)
          }}
          onBlur={() => {
            setIsFocused(false)
            parseTypedInput()
          }}
          style={{ outline: 'none' }}
        />
      </div>

      {/* Show validation */}
      {errorCount > 0 && (
        <p className="text-destructive mt-2 mb-4 text-right">
          <FontAwesomeIcon icon={faTriangleExclamation} className="pr-1" />

          {`${errorCount} ${errorCount === 1 ? 'error' : 'errors'}. `}

          <Button
            variant="link"
            className="p-0"
            onClick={removeInvalidEmails}
            size="sm"
          >
            Remove all items with errors
          </Button>
        </p>
      )}
    </>
  )
}

export default EmailAddresses
